import { useState, useMemo } from 'react'
import debounce from 'lodash/fp/debounce'

// As useState, but changes to the value are debounced.
// Adds a third returned value that reflects changes immediately.
export function useDebouncedState<S>(
  initialState: S | (() => S),
  wait?: number
) {
  const debouncedCall = useMemo(
    () => debounce(wait ?? 250, (fn) => fn()),
    [wait]
  )

  const [rawValue, setRawValue] = useState<S>(initialState)
  const [debouncedValue, setDebouncedValue] = useState<S>(initialState)

  const setValue: typeof setRawValue = (update) => {
    setRawValue(update)
    debouncedCall(() => setDebouncedValue(update))
  }

  return [debouncedValue, setValue, rawValue] as [
    debouncedValue: S,
    setValue: typeof setValue,
    rawValue: S,
  ]
}
